import axios from "axios";
import React from "react";
import { KeyboardEvent } from "react";
import { useState } from "react";

import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";

import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckIcon from "@mui/icons-material/Check";

import GetCSRFToken from "./GetCSRFToken";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import PagePaper from "../PageComponents/PagePaper";

import PersonalizationConfig from "../PersonalizationComponents/PersonalizationConfig";

const ErrorStringIncorrect = "Username or password is incorrect";
const ErrorStringResetOrRegister = "Forgot username or password? Reset or Register for an account below.";

type LoginDialogProps = {
  openRegisterDialog: () => void;
  openResetDialog: () => void;
  setUserFullName: (newName: string | undefined) => void;
  setUserConfig: (newConfig: PersonalizationConfig) => void;
  username: string;
  setUsername: (newUsername: string) => void;
};

function LoginDialog(props: LoginDialogProps) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");

  const [success, setSuccess] = useState(false);

  const [loading, setLoading] = useState(false);
  const [failedLoginCount, setFailedLoginCount] = useState(0);

  const [showError, setShowError] = useState(false);
  // TODO: after 2 or 3 suggest a reset?

  const ErrorString = failedLoginCount > 2 ? ErrorStringResetOrRegister : ErrorStringIncorrect;

  function handleSubmit() {
    setLoading(true);
    axios
      .post(
        "/api/user/login",
        {
          username: props.username,
          password: password,
        },
        {
          headers: { "X-CSRFToken": GetCSRFToken() },
        }
      )
      .then((res) => {
        setSuccess(true);
        setLoading(false);
        setPassword("");
        props.setUserFullName(res.data["name"]);
        props.setUserConfig(res.data["config"]);
        navigate("/");
        setTimeout(() => setSuccess(false), 1000); // clear the successful signout state 1 second logout
        // TODO: something with this response, a log in successful?
        // TODO: propogate upward to force a reload?
        // TODO: flash a success message?
      })
      .catch((err) => {
        setPassword("");
        setFailedLoginCount(failedLoginCount + 1);
        setShowError(true);
        setLoading(false);
      });
  }

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (e.key === "Enter") {
      handleSubmit();
    }
  }

  return (
    <>
      <PagePaper title="Log In">
        <FormControl
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
          }}
        >
          <TextField
            variant="outlined"
            fullWidth
            value={props.username}
            onChange={(newValue) => {
              setShowError(false);
              props.setUsername(newValue.target.value);
            }}
            label="Username"
            autoComplete="username"
            autoFocus
            onKeyDown={catchEnterKeyDownToSubmit}
          />
          <TextField
            variant="outlined"
            type="password"
            error={showError}
            autoComplete="current-password"
            fullWidth
            value={password}
            onChange={(newValue) => {
              setShowError(false);
              setPassword(newValue.target.value);
            }}
            onKeyDown={catchEnterKeyDownToSubmit}
            label="Password"
            helperText={showError ? ErrorString : failedLoginCount > 3 ? ErrorStringResetOrRegister : null}
          />
        </FormControl>

        <LoadingButton
          variant="contained"
          color={success ? "success" : undefined}
          startIcon={success ? <CheckIcon /> : <PersonIcon />}
          loading={loading}
          disabled={false}
          onClick={handleSubmit}
        >
          {success ? "Logged In" : "Log in"}
        </LoadingButton>
        {failedLoginCount > 0 ? (
          <Button
            variant="text"
            size="small"
            sx={{ width: "fit-content", alignSelf: "center" }}
            onClick={() => {
              props.openResetDialog();
            }}
          >
            Forgot username or password?
          </Button>
        ) : null}
      </PagePaper>
      <Button
        startIcon={<PersonAddIcon />}
        onClick={() => {
          navigate("/register");
        }}
      >
        Create an account
      </Button>
    </>
  );
}

export default LoginDialog;
